import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

const CATEGORIES = ['전체', '이공·AI', '의학·생명', '인문·사회', '경상·경영', '예체능', '교육·복지'] as const

interface ArchiveCard {
  id: string
  title: string
  se: string
  tags: string[]
  author: string
  likes: number
  comments: number
  category: (typeof CATEGORIES)[number]
  excerpt: string
  /** true 면 실제 학생이 아니라 예시 작품임을 시각적으로 명시. Phase 1 mock 전용. */
  isExample?: boolean
}

const MOCK: ArchiveCard[] = [
  {
    id: '1',
    title: '우리 동네로 다시 본 코로나',
    se: '생명윤리 세특 · 데이터로 본 지역 격차',
    tags: ['의학·생명', '데이터', 'RAG'],
    author: '김민서',
    likes: 24,
    comments: 8,
    category: '의학·생명',
    excerpt: '"공공데이터로 동네별 확진자를 시계열로 그리고 싶다" 라는 한 줄에서 시작했어요…',
    isExample: true,
  },
  {
    id: '2',
    title: '고전소설 속 한 문단 챗봇',
    se: '국어 세특 · 1830년 한양으로 떠나는 답사',
    tags: ['인문', 'RAG', '챗봇'],
    author: '이서윤',
    likes: 17,
    comments: 5,
    category: '인문·사회',
    excerpt: '구운몽 본문을 작은 단락으로 쪼개서 임베딩하고, 질문에 맞는 문단을 골라 답하게 했어요…',
    isExample: true,
  },
  {
    id: '3',
    title: '우리 학교 매점 영수증 가계부',
    se: '경제 세특 · 8주 동안 내 소비 들여다보기',
    tags: ['경상', '메모리'],
    author: '박지호',
    likes: 31,
    comments: 12,
    category: '경상·경영',
    excerpt: '카테고리별 지출 비율을 도넛 차트로 그리는 부분에서 막혔는데, 풀어낸 과정은…',
    isExample: true,
  },
  {
    id: '4',
    title: '학교 점심 잔반 줄이기 챌린지',
    se: '환경 세특 · 행동경제학 실험',
    tags: ['환경', '예체능'],
    author: '최유나',
    likes: 19,
    comments: 6,
    category: '교육·복지',
    excerpt: '잔반 사진만으론 양을 재기 어려워서, 색깔 분포로 추정하는 방법을 만들어봤어요…',
    isExample: true,
  },
  {
    id: '5',
    title: '내신 4등급 친구 위한 진로 검사',
    se: '진로 세특 · 자기이해 검사 풀스택',
    tags: ['진로 미정', '심리'],
    author: '정시우',
    likes: 22,
    comments: 9,
    category: '인문·사회',
    excerpt: '홀랜드 6각형을 단순화해 10문항 검사로 만들고, 결과 페이지에 작품 추천을 붙였어요…',
    isExample: true,
  },
  {
    id: '6',
    title: '강아지 표정 사전',
    se: '동물행동학 세특 · CV 입문',
    tags: ['이공·AI', 'CV'],
    author: '강하준',
    likes: 28,
    comments: 11,
    category: '이공·AI',
    excerpt: '내 강아지 사진 300장을 라벨링해서 표정 분류기를 만들었고, 부정확한 결과의 이유를…',
    isExample: true,
  },
]

export const Route = createFileRoute('/archive')({
  component: ArchivePage,
})

function ArchivePage() {
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('전체')
  const [query, setQuery] = useState('')
  const [openCard, setOpenCard] = useState<ArchiveCard | null>(null)

  const filtered = MOCK.filter((m) => {
    if (category !== '전체' && m.category !== category) return false
    if (query && !m.title.includes(query) && !m.se.includes(query) && !m.author.includes(query)) {
      return false
    }
    return true
  })

  return (
    <div className="bg-bg min-h-[calc(100vh-56px)]">
      <div className="mx-auto max-w-[1200px] px-6 py-10">
        <header className="mb-8 flex flex-col gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="kr-tight text-[28px] font-extrabold tracking-tight">친구들 작품</h1>
              <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-bold" style={{ color: 'var(--color-accent-deep)' }}>
                예시 데이터
              </span>
            </div>
            <p className="mt-1 text-[14px] text-text-2">
              친구들이 어디서 막혔고, 어떻게 풀었고, 무엇을 배웠는지 볼 수 있어요. AI와 나눈 메시지 전체는 공개되지 않아요.
            </p>
            <p className="mt-1 text-[12px] text-text-3">
              아직 베타 기간이라 아래 카드는 예시예요. 학생들이 작품을 공개하면 여기로 올라와요.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                    category === c
                      ? 'bg-primary-soft text-primary'
                      : 'bg-surface-alt text-text-2 hover:bg-surface hover:text-text'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative">
              <input
                type="search"
                placeholder="제목·닉네임·세특 키워드"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-64 rounded-lg border border-border-strong bg-white px-3 py-2 text-[13px] outline-none focus:border-primary focus:shadow-[0_0_0_4px_rgba(49,130,246,0.12)]"
              />
            </div>
          </div>
        </header>

        {filtered.length === 0 ? (
          <div className="grid h-60 place-items-center rounded-2xl border border-dashed border-border-strong bg-white text-[14px] text-text-3">
            아직 보여줄 작품이 없어요. 카테고리나 검색어를 바꿔봐요.
          </div>
        ) : (
          <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => setOpenCard(c)}
                className="archive-card text-left relative"
              >
                <span className="absolute top-3 left-3 z-10 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-text-3 shadow-sm">
                  예시
                </span>
                <div className="archive-thumb">
                  <div className="tool-badges">
                    <div className="tool-badge">A</div>
                    <div className="tool-badge">V</div>
                  </div>
                  {c.isExample && (
                    <span className="absolute left-3 top-3 rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-bold text-text-2">
                      예시
                    </span>
                  )}
                  <div className="preview-mini">
                    <div style={{ height: 6, width: '70%', background: 'var(--color-primary-soft)', borderRadius: 3 }} />
                    <div style={{ height: 5, width: '90%', background: 'var(--color-surface-alt)', borderRadius: 3 }} />
                    <div style={{ height: 5, width: '60%', background: 'var(--color-surface-alt)', borderRadius: 3 }} />
                    <div style={{ height: 5, width: '75%', background: 'var(--color-surface-alt)', borderRadius: 3 }} />
                  </div>
                </div>
                <div className="archive-card-body">
                  <h4>{c.title}</h4>
                  <div className="se-line">{c.se}</div>
                  <div className="archive-tags">
                    {c.tags.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center rounded-full bg-primary-soft px-2.5 py-1 text-[11px] font-semibold text-primary"
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                  <div className="archive-meta">
                    <span>❤ {c.likes}</span>
                    <span>💬 {c.comments}</span>
                    <span className="author">by {c.author}</span>
                  </div>
                </div>
              </button>
            ))}
          </section>
        )}

        <div className="mt-8 rounded-2xl bg-surface-alt p-5 text-[13px] leading-relaxed text-text-2">
          <div className="font-semibold text-text mb-2">🛡️ 안심하고 둘러보세요</div>
          <ul className="space-y-1 text-[12.5px]">
            <li>· 챗봇 대화는 작품 주인이 직접 고른 부분만 보여요.</li>
            <li>· 이름·학교·지역·연락처는 자동으로 가려져요.</li>
            <li>· AI가 자동으로 확인하고, 바이브타운이 한 번 더 확인한 작품만 올라와요.</li>
          </ul>
        </div>
      </div>

      {openCard && <ArchiveCardModal card={openCard} onClose={() => setOpenCard(null)} />}
    </div>
  )
}

function ArchiveCardModal({ card, onClose }: { card: ArchiveCard; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-6"
      style={{ animation: 'vibe-backdrop-in 0.2s ease-out' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[640px] overflow-hidden rounded-2xl bg-white shadow-lg"
        style={{ animation: 'vibe-modal-in 0.18s var(--ease-out-toss)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-48 bg-gradient-to-br from-primary-soft to-accent-soft" />
        <div className="space-y-4 p-7">
          <div>
            <h2 className="kr-tight text-[22px] font-extrabold tracking-tight">{card.title}</h2>
            <div className="mt-1 text-[13px] text-text-3">{card.se}</div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {card.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-full bg-primary-soft px-2.5 py-1 text-[11px] font-semibold text-primary"
              >
                #{t}
              </span>
            ))}
          </div>
          <div className="rounded-xl bg-surface-alt p-4 text-[14px] leading-relaxed text-text-2">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-3">여기서 헤맸어요</div>
            {card.excerpt}
          </div>
          <div className="flex items-center justify-between text-[13px] text-text-3">
            <span>
              ❤ {card.likes} · 💬 {card.comments}
            </span>
            <span className="font-semibold text-text-2">by {card.author}</span>
          </div>
          <button
            onClick={onClose}
            className="w-full rounded-xl bg-primary py-3 text-[14px] font-bold text-white hover:bg-primary-hover"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}
